import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, MessageCircle, Send, Bookmark, Share2, Music2, MoreHorizontal, Camera, ImageOff } from "lucide-react";
import { API } from "@/lib/api";

const CUT = { instagram: 125, tiktok: 150 };
const handle = (name) => (name || "cliente").toLowerCase().normalize("NFD").replace(/[^\w]+/g, "").slice(0, 24) || "cliente";
const countTags = (s) => (s.match(/#[^\s#]+/g) || []).length;

const useMedia = (mid) => {
  const [m, setM] = useState(null);
  useEffect(() => {
    if (!mid) return setM(null);
    let url;
    axios.get(`${API}/admin/sp/media/${mid}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setM({ url, video: r.data.type.startsWith("video/") }); })
      .catch(() => setM(null));
    return () => url && URL.revokeObjectURL(url);
  }, [mid]);
  return m;
};

const Media = ({ media }) => media
  ? (media.video ? <video src={media.url} muted loop autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
    : <img src={media.url} alt="" className="absolute inset-0 w-full h-full object-cover" />)
  : <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/40 text-[10px] font-mono uppercase"><ImageOff className="w-6 h-6" />Nessun media</div>;

const Caption = ({ text, cut, more, testid }) => {
  const short = text.length > cut;
  return <p data-testid={testid} className="text-[11px] leading-snug whitespace-pre-line break-words">{short ? text.slice(0, cut).trimEnd() : text}{short && <span className="text-white/60">… {more}</span>}</p>;
};

const Side = ({ icons }) => (
  <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4">{icons.map(([I, n], i) => (
    <div key={i} className="flex flex-col items-center text-[9px]"><I className="w-5 h-5" strokeWidth={1.8} />{n}</div>))}</div>
);

const Phone = ({ children, label, testid }) => (
  <div data-testid={testid} className="space-y-1.5">
    <div className="text-[10px] uppercase font-mono text-[#6b7280]">{label}</div>
    <div className="relative w-[220px] aspect-[9/16] rounded-[26px] border-[6px] border-[#111] bg-black overflow-hidden text-white shadow-lg">{children}</div>
  </div>
);

const Shade = () => <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/30 pointer-events-none" />;

const Instagram = ({ media, user, text, story, testid }) => (
  <Phone label={story ? "Instagram · Stories" : "Instagram · Reel"} testid={testid}>
    <Media media={media} /><Shade />
    {story ? (
      <>
        <div className="absolute top-2 inset-x-2 h-0.5 bg-white/40 rounded"><div className="h-full w-1/3 bg-white rounded" /></div>
        <div className="absolute top-4 left-2 flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-6 h-6 rounded-full bg-white/30 border border-white" />{user}<span className="text-white/60 font-normal">1 h</span></div>
        <div className="absolute bottom-3 inset-x-2 flex items-center gap-2"><div className="flex-1 h-8 rounded-full border border-white/60 text-[10px] flex items-center px-3 text-white/70">Invia messaggio</div><Heart className="w-5 h-5" /><Send className="w-5 h-5" /></div>
        <p data-testid={`${testid}-story-note`} className="absolute bottom-14 inset-x-3 text-[9px] text-center text-white/70">Nelle Stories la caption non compare sul video</p>
      </>
    ) : (
      <>
        <div className="absolute top-3 left-3 right-3 flex justify-between text-sm font-semibold">Reels<Camera className="w-5 h-5" /></div>
        <Side icons={[[Heart, "1.204"], [MessageCircle, "38"], [Send, ""], [MoreHorizontal, ""]]} />
        <div className="absolute bottom-3 left-3 right-12 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-6 h-6 rounded-full bg-white/30 border border-white" />{user}<span className="border border-white/70 rounded px-1.5 py-px text-[9px]">Segui</span></div>
          <Caption text={text} cut={CUT.instagram} more="altro" testid={`${testid}-caption`} />
          <div className="flex items-center gap-1 text-[9px] text-white/80"><Music2 className="w-3 h-3" />{user} · Audio originale</div>
        </div>
      </>
    )}
  </Phone>
);

const TikTok = ({ media, user, text, testid }) => (
  <Phone label="TikTok" testid={testid}>
    <Media media={media} /><Shade />
    <div className="absolute top-3 inset-x-0 text-center text-[11px] font-semibold"><span className="text-white/60">Seguiti</span><span className="mx-2 text-white/40">|</span>Per te</div>
    <Side icons={[[Heart, "8.731"], [MessageCircle, "112"], [Bookmark, "420"], [Share2, "64"]]} />
    <div className="absolute bottom-3 left-3 right-12 space-y-1.5">
      <div className="text-[12px] font-semibold">@{user}</div>
      <Caption text={text} cut={CUT.tiktok} more="altro" testid={`${testid}-caption`} />
      <div className="flex items-center gap-1 text-[9px] text-white/80"><Music2 className="w-3 h-3" />suono originale · {user}</div>
    </div>
  </Phone>
);

const Warnings = ({ text, hashtags, platforms }) => {
  const w = [];
  if (platforms.includes("instagram") && countTags(hashtags) > 30) w.push("Instagram accetta al massimo 30 hashtag");
  if (text.length > 2200) w.push(`Testo troppo lungo: ${text.length}/2200 caratteri`);
  return w.length ? <ul data-testid="sp-preview-warnings" className="text-[11px] text-[#b45309] space-y-0.5">{w.map((x) => <li key={x}>· {x}</li>)}</ul> : null;
};

export const PostPreview = ({ p, caption, hashtags }) => {
  const media = useMedia(p.media_id);
  const user = handle(p.client_name);
  const text = `${caption}\n\n${hashtags}`.trim();
  const id = p.id;
  return (
    <div data-testid={`sp-preview-${id}`} className="space-y-2">
      <div className="flex flex-wrap gap-4">
        {p.platforms.includes("instagram") && <Instagram media={media} user={user} text={text} story={p.content_type === "story"} testid={`sp-preview-ig-${id}`} />}
        {p.platforms.includes("tiktok") && <TikTok media={media} user={user} text={text} testid={`sp-preview-tt-${id}`} />}
      </div>
      <div data-testid={`sp-preview-count-${id}`} className="text-[10px] font-mono text-[#6b7280]">{p.content_type === "story" && !p.platforms.includes("tiktok") ? "Stories: testo e hashtag non compaiono sul contenuto" : `${text.length}/2200 caratteri · ${countTags(hashtags)} hashtag`}</div>
      <Warnings text={text} hashtags={hashtags} platforms={p.platforms} />
    </div>
  );
};
